import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calculator, RefreshCw, Target, TrendingUp } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_V2 = `${BACKEND_URL}/api/v2`;

const MiniPriceChart = ({ pair, avgBuyPrice }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchChart = async () => {
      try {
        const res = await axios.get(`${API_V2}/market/history`, {
          params: { pair, minutes: 1440, limit: 96 },
          withCredentials: true
        });
        if (cancelled) return;
        const history = res.data?.history || [];
        const formatted = history.map(c => ({
          time: new Date(c.timestamp || c.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          price: c.close || c.price || 0,
        }));
        setChartData(formatted);
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchChart();
    return () => { cancelled = true; };
  }, [pair]);

  const { minPrice, maxPrice, isUp } = useMemo(() => {
    if (chartData.length < 2) return { minPrice: 0, maxPrice: 0, isUp: true };
    const prices = chartData.map(d => d.price).filter(p => p > 0);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return { minPrice: min, maxPrice: max, isUp: prices[prices.length - 1] >= prices[0] };
  }, [chartData]);

  if (loading) {
    return (
      <div className="h-[100px] flex items-center justify-center text-[#008F11] text-sm">
        Loading chart...
      </div>
    );
  }

  if (chartData.length < 2) return null;

  const chartColor = isUp ? '#00FF41' : '#FF4444';
  const gradientId = `grad-${pair.replace(/[^a-zA-Z]/g, '')}`;

  return (
    <div className="h-[120px] mt-2 mb-2" data-testid={`price-chart-${pair}`}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={chartColor} stopOpacity={0.3} />
              <stop offset="95%" stopColor={chartColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="time"
            tick={{ fill: '#555', fontSize: 10 }}
            axisLine={{ stroke: '#333' }}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            domain={[minPrice * 0.998, maxPrice * 1.002]}
            tick={{ fill: '#555', fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={v => `$${v >= 1 ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : v.toFixed(6)}`}
            width={65}
          />
          <Tooltip
            contentStyle={{ background: '#0a0a0a', border: '1px solid #003B00', fontSize: 12, fontFamily: 'monospace' }}
            labelStyle={{ color: '#888' }}
            formatter={v => [`$${v.toLocaleString(undefined, { maximumFractionDigits: v >= 1 ? 2 : 8 })}`, 'Price']}
          />
          {avgBuyPrice > 0 && avgBuyPrice >= minPrice * 0.95 && avgBuyPrice <= maxPrice * 1.05 && (
            <ReferenceLine
              y={avgBuyPrice}
              stroke="#00bcd4"
              strokeDasharray="4 4"
              strokeWidth={1.5}
              label={{ value: `AVG $${avgBuyPrice >= 1 ? avgBuyPrice.toFixed(2) : avgBuyPrice.toFixed(6)}`, fill: '#00bcd4', fontSize: 10, position: 'right' }}
            />
          )}
          <Area
            type="monotone"
            dataKey="price"
            stroke={chartColor}
            strokeWidth={1.5}
            fill={`url(#${gradientId})`}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
      <div className="text-xs text-gray-600 text-center -mt-1">24H PRICE — DASHED LINE = YOUR AVG BUY</div>
    </div>
  );
};

const fmtPrice = (v) => {
  if (typeof v !== 'number') return v;
  return v < 0.01 ? v.toFixed(8) : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const HealthBar = ({ health, distancePct }) => {
  const capped = Math.min(health, 100);
  const isProfitable = health >= 100;
  const isNegative = distancePct < 0;

  let barColor, bgColor, label;
  if (isProfitable) {
    barColor = 'bg-[#00FF41]'; bgColor = 'bg-green-950/40'; label = 'READY';
  } else if (health >= 70) {
    barColor = 'bg-yellow-400'; bgColor = 'bg-yellow-950/20'; label = 'CLOSE';
  } else if (health >= 30) {
    barColor = 'bg-orange-500'; bgColor = 'bg-orange-950/20'; label = 'WARMING';
  } else if (isNegative) {
    barColor = 'bg-red-500'; bgColor = 'bg-red-950/30'; label = 'UNDERWATER';
  } else {
    barColor = 'bg-red-400'; bgColor = 'bg-red-950/20'; label = 'EARLY';
  }

  return (
    <div className="flex items-center gap-2 w-full" data-testid="lot-health-bar">
      <div className={`relative h-2 flex-1 rounded-full overflow-hidden ${bgColor} border border-[#003B00]/50`}>
        <div
          className={`absolute left-0 top-0 h-full rounded-full transition-all duration-700 ${barColor}`}
          style={{ width: `${Math.max(capped, 3)}%` }}
        />
      </div>
      <span className={`text-xs font-bold tracking-wider w-[80px] text-right shrink-0 ${
        isProfitable ? 'text-[#00FF41]' : isNegative ? 'text-red-400' : 'text-orange-400'
      }`}>
        {label}
      </span>
    </div>
  );
};

const LotHealthSummary = ({ lots, pair }) => {
  if (!lots || lots.length === 0) return null;
  
  const ready = lots.filter(l => l.health >= 100).length;
  const warming = lots.filter(l => l.health >= 30 && l.health < 100).length;
  const underwater = lots.filter(l => l.health < 30).length;
  const avgHealth = lots.reduce((s, l) => s + l.health, 0) / lots.length;

  return (
    <div className="grid grid-cols-4 gap-2 mb-3" data-testid={`lot-health-summary-${pair}`}>
      <div className="bg-[#001100] border border-[#003B00]/50 p-2.5 text-center">
        <div className="text-xs text-gray-500 uppercase">Avg Health</div>
        <div className={`text-lg font-bold font-mono ${avgHealth >= 100 ? 'text-[#00FF41]' : avgHealth >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
          {avgHealth.toFixed(0)}%
        </div>
      </div>
      <div className="bg-[#001100] border border-green-900/30 p-2.5 text-center">
        <div className="text-xs text-gray-500 uppercase">Ready</div>
        <div className="text-lg font-bold font-mono text-[#00FF41]">{ready}</div>
      </div>
      <div className="bg-[#001100] border border-orange-900/30 p-2.5 text-center">
        <div className="text-xs text-gray-500 uppercase">Warming</div>
        <div className="text-lg font-bold font-mono text-orange-400">{warming}</div>
      </div>
      <div className="bg-[#001100] border border-red-900/30 p-2.5 text-center">
        <div className="text-xs text-gray-500 uppercase">Underwater</div>
        <div className="text-lg font-bold font-mono text-red-400">{underwater}</div>
      </div>
    </div>
  );
};

/* ─── Card-style lot view (replaces tiny table) ─── */
const LotCard = ({ lot, idx, pair, currentPrice, sellingLot, onSell }) => {
  const currentVal = lot.amount * currentPrice;
  const lotPL = currentVal - lot.cost;
  const lotPLPct = lot.cost > 0 ? ((lotPL / lot.cost) * 100) : 0;
  const health = lot.health ?? 0;
  const distancePct = lot.distance_pct ?? 0;
  const targetPrice = lot.target_price ?? lot.price;
  const targetDist = lot.target_distance_pct ?? 0;
  const isProfitable = health >= 100;

  return (
    <div
      className={`border rounded p-3 ${isProfitable ? 'border-green-800/50 bg-green-950/10' : 'border-[#003B00]/50 bg-[#0a0a0a]'}`}
      data-testid={`lot-card-${pair}-${idx}`}
    >
      {/* Row 1: Lot # + Health bar */}
      <div className="flex items-center gap-3 mb-2">
        <span className="text-sm font-bold text-gray-400 w-6 shrink-0">#{idx + 1}</span>
        <div className="flex-1">
          <HealthBar health={health} distancePct={distancePct} />
        </div>
      </div>

      {/* Row 2: Key numbers in a grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 mb-2">
        <div>
          <div className="text-xs text-gray-500">BUY PRICE</div>
          <div className="text-sm font-mono text-cyan-400">${fmtPrice(lot.price)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">TARGET</div>
          <div className="text-sm font-mono text-yellow-500">${fmtPrice(targetPrice)}</div>
          {!isProfitable && (
            <div className="text-xs text-gray-500">{targetDist > 0 ? `+${targetDist.toFixed(1)}% away` : 'reached'}</div>
          )}
        </div>
        <div>
          <div className="text-xs text-gray-500">AMOUNT</div>
          <div className="text-sm font-mono text-white">{lot.amount.toFixed(8)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">COST / VALUE</div>
          <div className="text-sm font-mono text-white">${lot.cost.toFixed(2)} / <span className="text-yellow-400">${currentVal.toFixed(2)}</span></div>
        </div>
      </div>

      {/* Row 3: P/L + Date + Sell */}
      <div className="flex items-center justify-between">
        <div>
          <span className={`text-base font-bold font-mono ${lotPL >= 0 ? 'text-[#00FF41]' : 'text-red-400'}`}>
            {lotPL >= 0 ? '+' : ''}${lotPL.toFixed(2)}
          </span>
          <span className={`text-sm font-mono ml-1.5 ${lotPLPct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            ({lotPLPct >= 0 ? '+' : ''}{lotPLPct.toFixed(1)}%)
          </span>
        </div>
        <span className="text-xs text-gray-500 font-mono">
          {lot.timestamp ? new Date(lot.timestamp).toLocaleDateString() : '-'}
        </span>
        <Button
          size="sm"
          onClick={() => onSell(pair, idx, lot)}
          disabled={!!sellingLot}
          className={`h-7 px-3 text-xs border disabled:opacity-50 ${
            isProfitable
              ? 'bg-green-900/60 text-green-300 border-green-700 hover:bg-green-800'
              : 'bg-red-900/60 text-red-300 border-red-700 hover:bg-red-800'
          }`}
          data-testid={`sell-lot-${pair}-${idx}`}
        >
          {sellingLot === `${pair}-${idx}` ? '...' : isProfitable ? (
            <span className="flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" />SELL</span>
          ) : 'SELL'}
        </Button>
      </div>
    </div>
  );
};

export const CostBasisModal = ({ open, onOpenChange, playAlertSound }) => {
  const [costBasisData, setCostBasisData] = useState(null);
  const [costBasisLoading, setCostBasisLoading] = useState(false);
  const [sellingLot, setSellingLot] = useState(null);

  const fetchCostBasis = useCallback(async () => {
    setCostBasisLoading(true);
    try {
      const res = await axios.get(`${API_V2}/user/bot/cost-basis`, { withCredentials: true });
      setCostBasisData(res.data);
    } catch (err) {
      // silent
    } finally {
      setCostBasisLoading(false);
    }
  }, []);

  const handleSellLot = async (pair, lotIndex, lot) => {
    const key = `${pair}-${lotIndex}`;
    if (sellingLot) return;
    if (!window.confirm(`Sell lot #${lotIndex + 1} of ${pair}?\n${lot.amount.toLocaleString(undefined, {maximumFractionDigits: 8})} @ $${lot.price} (cost $${lot.cost.toFixed(2)})`)) return;
    setSellingLot(key);
    let sellSucceeded = false;
    try {
      const res = await axios.post(`${API_V2}/user/bot/sell-lot`, { pair, lot_index: lotIndex }, { withCredentials: true, timeout: 45000 });
      sellSucceeded = true;
      const usd = res.data?.usd_received ?? '?';
      const pl = res.data?.profit_loss != null ? res.data.profit_loss.toFixed(4) : '0.0000';
      toast.success(`Lot #${lotIndex + 1} sold for $${usd} (P/L: $${pl})`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 400 && detail && !detail.includes('Invalid response')) {
        toast.error(`Sell error: ${detail}`);
      } else {
        // Timeouts, 5xx, or parsing errors — the sell likely went through
        toast.warning('Sell is processing — refreshing data...', { duration: 4000 });
        sellSucceeded = true;
      }
    }
    if (sellSucceeded) {
      try { if (playAlertSound) playAlertSound('sell'); } catch {}
    }
    // Quick refresh then another after exchange settles
    await fetchCostBasis();
    await new Promise(r => setTimeout(r, 2000));
    await fetchCostBasis();
    setSellingLot(null);
  };

  useEffect(() => {
    if (open) fetchCostBasis();
  }, [open, fetchCostBasis]);

  useEffect(() => {
    if (!open) return;
    const intervalId = setInterval(() => {
      if (!sellingLot) fetchCostBasis();
    }, 5000);
    return () => clearInterval(intervalId);
  }, [open, sellingLot, fetchCostBasis]);

  const minProfitPct = costBasisData?.min_sell_profit_pct ?? null;
  const takerFeePct = costBasisData?.taker_fee_pct ?? null;
  const roundtripFeePct = costBasisData?.roundtrip_fee_pct ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black border border-[#008F11] max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-[#00FF41] flex items-center gap-2 text-lg">
            <Calculator className="h-5 w-5" />
            Cost Basis
            <span className="text-sm text-gray-500 font-normal ml-2">(auto-refresh: 5s)</span>
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">
            FIFO lot tracking with health indicators. {minProfitPct != null && (
              <span className="text-yellow-500 font-semibold">Sell target: {minProfitPct}% profit per lot.</span>
            )}{' '}
            {takerFeePct != null && (
              <span className="text-cyan-400">Fee tier: {takerFeePct}%/trade ({roundtripFeePct}% round-trip).</span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto min-h-0 pr-1" style={{ maxHeight: 'calc(90vh - 160px)' }}>
          {costBasisLoading && !costBasisData ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-[#008F11]" />
              <span className="ml-2 text-[#008F11]">Loading cost basis data...</span>
            </div>
          ) : costBasisData?.cost_basis && Object.keys(costBasisData.cost_basis).length > 0 ? (
            <div className="space-y-6">
              {Object.entries(costBasisData.cost_basis).map(([pair, data]) => (
                <div key={pair} className="border border-[#003B00] rounded p-4" data-testid={`cost-basis-pair-${pair}`}>
                  {/* Pair header */}
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-[#00FF41] font-bold text-xl">{pair}</h3>
                    <Badge className={`rounded-none text-sm px-2 py-0.5 ${data.unrealized_pnl >= 0 ? 'bg-green-900/30 text-[#00FF41] border-[#00FF41]' : 'bg-red-900/30 text-red-400 border-red-400'}`}>
                      {data.unrealized_pnl >= 0 ? '+' : ''}{data.unrealized_pnl_percent.toFixed(2)}%
                    </Badge>
                  </div>
                  
                  {/* Stats grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <div className="bg-[#001100] p-3 rounded">
                      <div className="text-gray-500 text-xs mb-0.5">BALANCE</div>
                      <div className="text-white font-mono text-sm">{data.current_balance.toLocaleString(undefined, {maximumFractionDigits: 8})}</div>
                    </div>
                    <div className="bg-[#001100] p-3 rounded">
                      <div className="text-gray-500 text-xs mb-0.5">AVG BUY PRICE</div>
                      <div className="text-cyan-400 font-mono text-sm">${fmtPrice(data.average_buy_price)}</div>
                    </div>
                    <div className="bg-[#001100] p-3 rounded">
                      <div className="text-gray-500 text-xs mb-0.5">CURRENT PRICE</div>
                      <div className="text-yellow-400 font-mono text-sm">${fmtPrice(data.current_price)}</div>
                    </div>
                    <div className="bg-[#001100] p-3 rounded">
                      <div className="text-gray-500 text-xs mb-0.5">UNREALIZED P/L</div>
                      <div className={`font-mono text-sm ${data.unrealized_pnl >= 0 ? 'text-[#00FF41]' : 'text-red-400'}`}>
                        {data.unrealized_pnl >= 0 ? '+' : ''}${data.unrealized_pnl.toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <MiniPriceChart pair={pair} avgBuyPrice={data.average_buy_price || 0} />

                  {/* Lot Health Summary */}
                  <LotHealthSummary lots={data.lots} pair={pair} />
                  
                  {/* Totals row */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="text-center">
                      <div className="text-gray-500 text-xs">TOTAL COST</div>
                      <div className="text-white font-mono text-base font-bold">${data.total_cost_basis.toLocaleString()}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-gray-500 text-xs">CURRENT VALUE</div>
                      <div className="text-white font-mono text-base font-bold">${data.current_value.toLocaleString()}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-gray-500 text-xs">LOTS</div>
                      <div className="text-purple-400 font-mono text-base font-bold">{data.lots_remaining}</div>
                    </div>
                  </div>
                  
                  {/* Lot cards */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-gray-400 font-semibold">BUY LOTS (FIFO - Oldest First)</span>
                    <Target className="h-3.5 w-3.5 text-yellow-500" />
                    <span className="text-xs text-yellow-500">TARGET = MIN SELL PRICE</span>
                  </div>
                  <div className="space-y-2">
                    {data.lots?.map((lot, idx) => (
                      <LotCard
                        key={idx}
                        lot={lot}
                        idx={idx}
                        pair={pair}
                        currentPrice={data.current_price}
                        sellingLot={sellingLot}
                        onSell={handleSellLot}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calculator className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No cost basis data available</p>
              <p className="text-sm mt-1">Make some trades first</p>
            </div>
          )}
        </div>
        
        <div className="flex-shrink-0 flex items-center justify-between mt-4 pt-4 border-t border-[#003B00]">
          <span className="text-sm text-gray-500">
            {costBasisData?.mode === 'live' ? 'LIVE' : 'SIM'} | {costBasisData?.generated_at ? new Date(costBasisData.generated_at).toLocaleTimeString() : ''}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={fetchCostBasis}
              disabled={costBasisLoading}
              className="border-[#008F11] text-[#008F11] hover:bg-[#003B00]"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${costBasisLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={() => onOpenChange(false)}
              className="bg-[#008F11] text-black hover:bg-[#00FF00]"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
